import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiCheck, FiArrowLeft } = FiIcons;

const TrialSection = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    presence: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const { error } = await supabase.from('leads').insert({
        full_name: formData.fullName,
        email: formData.email,
        social_handle: formData.presence,
        plan_selected: 'FREE',
        source: 'free_trial',
        intent_level: 'low'
      });

      if (error) throw error;
      setStatus('success');
      setFormData({ fullName: '', email: '', presence: '' });
    } catch (error) {
      console.error('Error submitting trial lead:', error);
      setStatus('error');
    }
  };

  const bullets = [
    "Real edits using our production pipeline",
    "No contracts or upfront payment",
    "Keep everything we create",
    "Continue only if it makes sense"
  ];

  return (
    // Removed overflow-hidden to allow the marquee to span full width over the curve
    <section id="trial" className="relative bg-[#BEF264] py-16 md:py-32 section-curve-up">
      {/* Marquee */}
      <div className="absolute top-0 left-0 w-full overflow-hidden whitespace-nowrap bg-black py-3 z-20">
        <motion.div animate={{ x: [0, -1000] }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }} className="flex items-center gap-12" >
          {[...Array(10)].map((_, i) => (
            <span key={i} className="text-[#BEF264] font-black uppercase tracking-[0.5em] text-[10px]"> NEW YEAR OFFER • 7-DAY FREE TRIAL • LIMITED SLOTS </span>
          ))}
        </motion.div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10 mt-6 md:mt-8 perspective-1000">
        <motion.div 
          className="relative w-full preserve-3d"
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Front Face */}
          <div className="relative backface-hidden bg-black border border-black/10 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-20 shadow-2xl">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                <span className="text-[#BEF264] font-black uppercase tracking-[0.4em] text-[10px] mb-4 md:mb-6 block"> RISK-FREE ENTRY </span>
                <h2 className="text-4xl md:text-8xl font-black text-white tracking-tighter leading-none mb-6 md:mb-8 uppercase italic"> 7-Day <span className="not-italic text-[#BEF264]">Free Trial</span> </h2>
                <p className="text-white/80 text-base md:text-2xl font-medium tracking-tight mb-10 md:mb-12 w-full mx-auto leading-relaxed md:whitespace-nowrap"> Experience our workflow, quality, and consistency before committing. </p>
                <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12 md:mb-16 max-w-3xl mx-auto">
                  {bullets.map((bullet, idx) => (
                    <div key={idx} className="flex items-center gap-4 text-left p-4 rounded-2xl bg-white/5 border border-white/5">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#BEF264]/10 flex items-center justify-center">
                        <SafeIcon icon={FiCheck} className="text-[#BEF264] w-3.5 h-3.5" />
                      </div>
                      <span className="text-white font-black uppercase tracking-widest text-[9px] md:text-[11px]"> {bullet} </span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center gap-6">
                  <button 
                    onClick={() => setIsFlipped(true)}
                    className="w-full md:w-auto relative group px-12 py-6 bg-[#BEF264] text-black rounded-full font-black uppercase tracking-[0.3em] text-[11px] overflow-hidden transition-all shadow-[0_0_40px_-10px_rgba(190,242,100,0.3)] hover:shadow-[0_0_60px_-10px_rgba(190,242,100,0.5)]"
                  >
                    <motion.div className="absolute inset-0 bg-white/40" animate={{ x: ['-100%', '200%'] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }} style={{ skewX: -20, filter: 'blur(10px)' }} />
                    <span className="relative z-10">Start the Free Trial</span>
                  </button>
                  <div className="flex items-center gap-4 opacity-30">
                    <div className="w-8 md:w-12 h-[1px] bg-white" />
                    <span className="text-[8px] font-black uppercase tracking-[0.4em] text-white">No Payment Method Required</span>
                    <div className="w-8 md:w-12 h-[1px] bg-white" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Back Face (Form) */}
          <div className="absolute inset-0 backface-hidden rotate-y-180 bg-black border border-white/20 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-20 shadow-2xl flex flex-col justify-center overflow-hidden">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center text-center animate-reveal h-full">
                <div className="w-20 h-20 rounded-full bg-[#BEF264]/10 flex items-center justify-center mb-8">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-[#BEF264]" />
                </div>
                <h3 className="text-white font-black uppercase tracking-widest text-3xl mb-4">You're In.</h3>
                <p className="text-white/60 text-lg font-medium tracking-wide max-w-md mb-12">
                  We've received your details. A producer will be in touch shortly to onboard you for your 7-day trial.
                </p>
                <button 
                  onClick={() => { setIsFlipped(false); setTimeout(() => setStatus('idle'), 500); }}
                  className="px-10 py-5 bg-white/5 hover:bg-[#BEF264] text-white hover:text-black rounded-xl text-xs font-black uppercase tracking-[0.2em] transition-all"
                >
                  Return Home
                </button>
              </div>
            ) : (
              <div className="max-w-xl mx-auto w-full h-full flex flex-col">
                <div className="flex items-center justify-between mb-8 md:mb-12">
                  <button onClick={() => setIsFlipped(false)} className="flex items-center gap-3 text-[#BEF264] font-black uppercase tracking-[0.3em] text-[10px] hover:text-white transition-colors">
                    <SafeIcon icon={FiArrowLeft} className="w-4 h-4" /> <span>Back</span>
                  </button>
                  <span className="text-white/20 font-black uppercase tracking-[0.3em] text-[10px]">Trial Access</span>
                </div>
                
                <div className="mb-8 md:mb-10">
                  <h3 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tighter mb-2">Claim Your Slot</h3>
                  <p className="text-white/50 text-sm md:text-base font-medium">Enter your details to begin the calibration process.</p>
                </div>

                <form className="space-y-6 flex-grow flex flex-col justify-center" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Identity *</label>
                    <input 
                      type="text" 
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required 
                      placeholder="Full Name" 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white text-base outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10 font-medium" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Communication *</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      placeholder="Email Address" 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white text-base outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10 font-medium" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#BEF264]/60 ml-1">Current Presence *</label>
                    <input 
                      type="text" 
                      name="presence"
                      value={formData.presence}
                      onChange={handleInputChange}
                      required
                      placeholder="Website URL or @handle" 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white text-base outline-none focus:border-[#BEF264] transition-colors placeholder:text-white/10 font-medium" 
                    />
                  </div>

                  {status === 'error' && (
                    <div className="text-red-500 text-xs font-bold uppercase tracking-widest text-center bg-red-500/10 py-3 rounded-xl border border-red-500/20">
                      Unable to submit. Please check your connection and try again.
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={status === 'submitting'}
                    className="w-full mt-4 bg-[#BEF264] text-black py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-xs hover:bg-white transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-[#BEF264]/20"
                  > 
                    {status === 'submitting' ? 'Processing...' : 'Start Free Trial'} 
                  </button>
                </form>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrialSection;